import {
  selectClient,
  selectPolicyApplications,
  selectPolicyApplicationsStatus,
  selectSession,
  selectSessionAdvisor,
} from '@client';
import {
  SessionType,
  UnitedStatesState,
  fundingPolicyTypeDefs,
  getFormIdForPolicyTypeAndState,
  policyTypeUnderwritingFormId,
} from '@shared';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { deriveApplicationStatus } from '../pages/PolicyApplicationDashboard';
import { useFormIdFromApi } from './useFormIdFromApi';

export function useClientPolicyApplication() {
  const { clientId, clientPolicyApplicationId } = useParams<{
    clientId?: string;
    clientPolicyApplicationId?: string;
  }>();

  const client = useSelector(selectClient);
  const policyApplications = useSelector(selectPolicyApplications);
  const policyApplicationsStatus = useSelector(selectPolicyApplicationsStatus);
  const currentAdvisor = useSelector(selectSessionAdvisor);
  const { sessionType } = useSelector(selectSession);

  const policyApplication = useMemo(
    () =>
      policyApplications.find(app => app.clientPolicyApplicationId === clientPolicyApplicationId),
    [policyApplications, clientPolicyApplicationId],
  );

  const fundingSourceId = policyApplication?.fundingSourceId;
  const policyFundingSource = useMemo(() => {
    if (!fundingSourceId || !client?.multipleFundingSources?.ltcPolicy) {
      return undefined;
    }
    return client.multipleFundingSources.ltcPolicy[fundingSourceId];
  }, [fundingSourceId, client]);
  const policyType = policyApplication?.fundingPolicyType;
  const fundingSourceDef = policyType ? fundingPolicyTypeDefs[policyType] : null;
  const steps = policyApplication?.steps;
  const applicationStatus = policyApplication
    ? deriveApplicationStatus(policyApplication).status
    : null;

  const underwritingFormId = policyType ? policyTypeUnderwritingFormId[policyType] : undefined;

  // Fetch application form ID from API
  const { formId: applicationFormId, isLoading: isLoadingFormId } = useFormIdFromApi({
    stateCode: policyApplication?.stateOfResidenceAbbreviation || 'default',
    formType: 'application-form',
    fundingPolicyTypeId: policyType,
    enabled: !!policyApplication?.stateOfResidenceAbbreviation && !!policyType,
  });

  //Removing the fallback as we only have stale fallback forms only for NW and OA.
  // Also, fallbacks do not support inactive forms feature

  // Fallback to static form ID if API fails or is loading
  // const staticApplicationFormId =
  //   policyApplication?.stateOfResidenceAbbreviation && policyType
  //     ? getFormIdForPolicyTypeAndState(
  //         policyType,
  //         policyApplication.stateOfResidenceAbbreviation as keyof typeof UnitedStatesState,
  //         'application-form',
  //       )
  //     : undefined;

  const isWizardSession = sessionType === SessionType.wizard;

  const currentStep = useMemo(() => {
    if (!steps) return null;

    // Find all relevant steps once
    const inProgressStep = steps.find(step => step.status === 'in_progress');
    const errorStep = steps.find(step => step.status === 'error');
    const notStartedStep = steps.find(step => step.status === 'not_started');
    const blockedStep = steps.find(step => step.status === 'blocked');
    const applicationFormStep = steps.find(step => step.stepType === 'application-form');
    const downloadIllustrationStep = steps.find(
      step => step.stepType === 'download-policy-illustration',
    );

    // Exception case: Require illustration before signing
    // Only redirect if application form is completed/skipped AND we're at the signing step
    // BUT: Allow wizard users to skip illustration and proceed to signing
    const shouldRequireIllustrationBeforeSigning =
      !isWizardSession &&
      notStartedStep?.stepType === 'sign-application-form' &&
      downloadIllustrationStep?.status === 'skipped' &&
      applicationFormStep?.status !== 'in_progress' &&
      applicationFormStep?.status !== 'not_started';

    if (shouldRequireIllustrationBeforeSigning && downloadIllustrationStep) {
      return downloadIllustrationStep;
    }

    // Standard priority order
    if (inProgressStep) return inProgressStep;
    if (errorStep) return errorStep;
    if (notStartedStep) return notStartedStep;
    if (blockedStep) return blockedStep;

    return steps[0];
  }, [steps, isWizardSession]);

  // Derive if illustration is needed before signing based on current step
  // Wizard users are exempt from this requirement
  const needsIllustrationBeforeSigning =
    !isWizardSession &&
    currentStep?.stepType === 'download-policy-illustration' &&
    steps?.find(s => s.stepType === 'download-policy-illustration')?.status === 'skipped';

  const allStepsCompleted =
    steps?.every(step => step.status === 'completed' || step.status === 'skipped') || false;

  const isLoading =
    policyApplicationsStatus === 'fetching' ||
    policyApplicationsStatus === 'creatingPolicyApplication' ||
    (!policyApplication && policyApplicationsStatus !== 'error');

  const clientInfo = useMemo(
    () => ({
      primaryName: `${client.clientFirstName} ${client.clientLastName}`,
      primaryEmail: client.clientEmail,
      primaryUserId: client.clientId,
      secondaryName: client.mutableClientPartner
        ? `${client.mutableClientPartner.clientFirstName} ${client.mutableClientPartner.clientLastName}`
        : undefined,
      secondaryEmail: client.mutableClientPartner?.clientEmail,
      secondaryUserId: client.mutableClientPartner?.clientId,
      advisorName: currentAdvisor
        ? `${currentAdvisor.advisorFirstName} ${currentAdvisor.advisorLastName}`
        : undefined,
      advisorEmail: currentAdvisor?.advisorEmail,
      advisorUserId: currentAdvisor?.advisorId,
    }),
    [client, currentAdvisor],
  );

  return {
    // Route params
    clientId,
    clientPolicyApplicationId,

    // Data
    client,
    policyApplication,
    policyType,
    fundingSourceDef,
    steps,
    currentStep,
    applicationStatus,
    currentAdvisor,
    clientInfo,
    policyFundingSource,

    // Form IDs
    underwritingFormId,
    applicationFormId,

    // State flags
    isLoading: isLoading || isLoadingFormId,
    allStepsCompleted,
    needsIllustrationBeforeSigning,

    // Status
    policyApplicationsStatus,
  };
}
